import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardMedia from "@material-ui/core/CardMedia";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";

// Dynamically import all images from images/misc
function importAll(r) {
  return r.keys().map((key) => {
    const module = r(key);
    const src = module.default || module;
    // key looks like './filename.jpg' -> strip './' and extension
    const base = key.replace(/\.\//, "").replace(/\.[^/.]+$/, "");
    // convert filename to a title: replace -/_ with space and capitalize words
    const title = base
      .replace(/[-_]+/g, " ")
      .split(" ")
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
    return { src, title };
  });
}

const images = importAll(
  require.context("../images/misc", false, /\.(png|jpe?g|svg)$/)
);

const videos = importAll(
  require.context("../images/misc", false, /\.(mp4|webm|mov|avi)$/)
);

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: "#000",
    minHeight: "100vh",
    paddingBottom: theme.spacing(6),
  },
  titleBox: {
    padding: theme.spacing(2),  
    marginBottom: theme.spacing(4),
    textAlign: "center",
    width: "fit-content",
    margin: "0 auto",
    borderRadius: 12,
  },
  titleText: {
    color: "white",
  },
  cardContainer: {
    maxWidth: 700,
    margin: "1.5rem auto",
  },
  media: {
    height: 360,
    objectFit: "cover",
  },
  cardTitle: {
    textAlign: "center",
  },
}));

const Miscellaneous = () => {
  const classes = useStyles();
  const [validVideos, setValidVideos] = React.useState([]);
  const [failedVideos, setFailedVideos] = React.useState(new Set());

  React.useEffect(() => {
    // Check which videos can be loaded by testing them
    const checkVideos = async () => {
      const valid = [];
      for (const video of videos) {
        try {
          const response = await fetch(video.src, { method: "HEAD" });
          if (response.ok) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.startsWith("video/")) {
              valid.push(video);
            }
          }
        } catch (error) {
          // Skip videos that can't be loaded
        }
      }
      setValidVideos(valid);
    };
    
    if (videos.length > 0) {
      checkVideos();
    }
  }, []);

  const handleVideoError = (videoSrc) => {
    setFailedVideos((prev) => new Set(prev).add(videoSrc));
  };

  // Combine and sort images and videos by title
  const allMedia = React.useMemo(() => {
    const combined = [
      ...images.map((img) => ({ ...img, type: "image" })),
      ...validVideos
        .filter((video) => !failedVideos.has(video.src))
        .map((video) => ({ ...video, type: "video" })),
    ];
    return combined.sort((a, b) => a.title.localeCompare(b.title));
  }, [validVideos, failedVideos]);

  return (
    <Box component="div" className={classes.mainContainer}>
      <Box className={classes.titleBox}>
        <Typography variant="h4" className={classes.titleText}>
          <span>✨</span>Miscellaneous Small Projects<span>✨</span>
        </Typography>
      </Box>

      <Grid container justifyContent="center">
        {allMedia.map((media, i) => (
          <Grid item xs={12} sm={10} md={6} key={`${media.type}-${i}`}>
            <Card className={classes.cardContainer}>
              {media.type === "image" ? (
                <a href={media.src} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                  <CardActionArea>
                    <CardMedia
                      component="img"
                      className={classes.media}
                      image={media.src}
                      title={media.title}
                    />
                    <CardContent>
                      <Typography variant="h5" className={classes.cardTitle}>
                        {media.title}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </a>
              ) : (
                <CardActionArea>
                  <video
                    className={classes.media}
                    controls
                    style={{ width: "100%", height: 360, objectFit: "cover" }}
                    onError={() => handleVideoError(media.src)}
                  >
                    <source src={media.src} />
                    Your browser does not support the video tag.
                  </video>
                  <CardContent>
                    <Typography variant="h5" className={classes.cardTitle}>
                      {media.title}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Miscellaneous;
